// Certificate template config (12a). The backend certificate config only accepts
// a flat `contentOptions: Record<string, boolean>` (ConfigCertificateDto), so the
// whole template design is encoded as booleans: one `tpl_<id>` flag for the chosen
// template, one `field_<id>` flag per merge field, plus issue-on-pass / QR flags.
// Merge tokens are [bracket]-style (never {{ }}).

export type CertTemplateId = 'classic' | 'modern' | 'minimal' | 'elegant';

export interface CertTemplate {
  id: CertTemplateId;
  nameKey: string;
  /** Preview accent (outer + inner border) — a certificate is a fixed white artifact. */
  outer: string;
  inner: string;
  accentText: string;
}

export const CERT_TEMPLATES: CertTemplate[] = [
  { id: 'classic', nameKey: 'cert_template_classic', outer: '#0A1628', inner: '#1D4ED8', accentText: '#1D4ED8' },
  { id: 'modern', nameKey: 'cert_template_modern', outer: '#1D4ED8', inner: '#93C5FD', accentText: '#1D4ED8' },
  { id: 'minimal', nameKey: 'cert_template_minimal', outer: '#111827', inner: '#9CA3AF', accentText: '#111827' },
  { id: 'elegant', nameKey: 'cert_template_elegant', outer: '#4338CA', inner: '#C7D2FE', accentText: '#4338CA' },
];

export interface MergeField {
  id: string;
  token: string;
  labelKey: string;
}

// The six clickable merge fields; `token` is what renders on the certificate.
export const MERGE_FIELDS: MergeField[] = [
  { id: 'student_name', token: '[student_name]', labelKey: 'cert_field_student_name' },
  { id: 'score', token: '[score]', labelKey: 'cert_field_score' },
  { id: 'date', token: '[date]', labelKey: 'cert_field_date' },
  { id: 'test_name', token: '[test_name]', labelKey: 'cert_field_test_name' },
  { id: 'group', token: '[group]', labelKey: 'cert_field_group' },
  { id: 'cert_id', token: '[cert_id]', labelKey: 'cert_field_cert_id' },
];

const TPL_PREFIX = 'tpl_';
const FIELD_PREFIX = 'field_';
export const OPT_ISSUE_ON_PASS = 'issue_on_pass';
export const OPT_QR = 'qr_enabled';

export interface CertState {
  template: CertTemplateId;
  fields: Record<string, boolean>;
  issueOnPass: boolean;
  qrEnabled: boolean;
}

const DEFAULT_FIELDS = (): Record<string, boolean> =>
  MERGE_FIELDS.reduce<Record<string, boolean>>((acc, f) => {
    acc[f.id] = f.id !== 'group' && f.id !== 'cert_id'; // sensible defaults
    return acc;
  }, {});

/** Read the persisted boolean map into structured cert state (with defaults). */
export function readCertState(contentOptions?: Record<string, boolean>): CertState {
  const opts = contentOptions ?? {};
  const template = (CERT_TEMPLATES.find((t) => opts[`${TPL_PREFIX}${t.id}`])?.id ?? 'classic') as CertTemplateId;
  const hasAnyField = MERGE_FIELDS.some((f) => `${FIELD_PREFIX}${f.id}` in opts);
  const fields = hasAnyField
    ? MERGE_FIELDS.reduce<Record<string, boolean>>((acc, f) => {
        acc[f.id] = Boolean(opts[`${FIELD_PREFIX}${f.id}`]);
        return acc;
      }, {})
    : DEFAULT_FIELDS();
  return {
    template,
    fields,
    issueOnPass: OPT_ISSUE_ON_PASS in opts ? Boolean(opts[OPT_ISSUE_ON_PASS]) : true,
    qrEnabled: Boolean(opts[OPT_QR]),
  };
}

/** Serialize cert state back to the flat boolean map the DTO accepts. */
export function writeCertState(state: CertState): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  CERT_TEMPLATES.forEach((t) => {
    out[`${TPL_PREFIX}${t.id}`] = t.id === state.template;
  });
  MERGE_FIELDS.forEach((f) => {
    out[`${FIELD_PREFIX}${f.id}`] = Boolean(state.fields[f.id]);
  });
  out[OPT_ISSUE_ON_PASS] = state.issueOnPass;
  out[OPT_QR] = state.qrEnabled;
  return out;
}
